/**
 * Путь внутри объёма (S9, запрос владельца 28.07): «Маршрут» в карточке
 * арендатора раньше всегда уводил на карту, хотя цель — в этом же здании.
 *
 * Внутриэтажного графа коридоров в данных нет (владелец рисует контуры, а не
 * осевые линии), поэтому путь ищем по СВОБОДНОМУ месту этажа: сетка, где
 * проходима ячейка внутри контура и вне чужих зон, дальше A*. Так линия идёт
 * коридорами, а не сквозь магазины.
 *
 * Чистые функции — тестируются без WebGL.
 */
import type { MPoint } from './model'
import type { TenantZone } from './zones'
import { inOutline } from './zones'

/** Шаг сетки, м. Мельче — ровнее путь, но дороже поиск (см. лимит ниже). */
const STEP_M = 2.2
/** Потолок числа ячеек: на слабом телефоне поиск должен оставаться мгновенным. */
const MAX_CELLS = 40_000

interface Grid {
  readonly cols: number
  readonly rows: number
  readonly step: number
  readonly minX: number
  readonly minY: number
  readonly walkable: Uint8Array
}

function boundsOf(outline: readonly MPoint[]): [number, number, number, number] {
  let minX = Number.POSITIVE_INFINITY
  let maxX = Number.NEGATIVE_INFINITY
  let minY = Number.POSITIVE_INFINITY
  let maxY = Number.NEGATIVE_INFINITY
  for (const [x, y] of outline) {
    minX = Math.min(minX, x)
    maxX = Math.max(maxX, x)
    minY = Math.min(minY, y)
    maxY = Math.max(maxY, y)
  }
  return [minX, minY, maxX, maxY]
}

/**
 * Сетка проходимости этажа. Зона ЦЕЛИ проходима — иначе до входа в магазин
 * дойти нельзя; остальные зоны — препятствия.
 */
export function walkGrid(
  outline: readonly MPoint[],
  zones: readonly TenantZone[],
  targetId: string | null,
): Grid | null {
  if (outline.length < 3) return null
  const [minX, minY, maxX, maxY] = boundsOf(outline)
  const w = maxX - minX
  const h = maxY - minY
  let step = STEP_M
  if ((w / step) * (h / step) > MAX_CELLS) step = Math.sqrt((w * h) / MAX_CELLS)
  const cols = Math.max(1, Math.ceil(w / step))
  const rows = Math.max(1, Math.ceil(h / step))
  const walkable = new Uint8Array(cols * rows)
  const blockers = zones.filter((z) => z.tenantId !== targetId)
  for (let cy = 0; cy < rows; cy++) {
    for (let cx = 0; cx < cols; cx++) {
      const p: MPoint = [minX + (cx + 0.5) * step, minY + (cy + 0.5) * step]
      if (!inOutline(p, outline)) continue
      if (blockers.some((z) => inOutline(p, z.outline))) continue
      walkable[cy * cols + cx] = 1
    }
  }
  return { cols, rows, step, minX, minY, walkable }
}

function cellOf(grid: Grid, p: MPoint): [number, number] {
  return [
    Math.min(grid.cols - 1, Math.max(0, Math.floor((p[0] - grid.minX) / grid.step))),
    Math.min(grid.rows - 1, Math.max(0, Math.floor((p[1] - grid.minY) / grid.step))),
  ]
}

function centerOf(grid: Grid, cx: number, cy: number): MPoint {
  return [grid.minX + (cx + 0.5) * grid.step, grid.minY + (cy + 0.5) * grid.step]
}

/** Индекс ячейки, если она внутри сетки и проходима. */
function walkableAt(grid: Grid, x: number, y: number): number | null {
  if (x < 0 || y < 0 || x >= grid.cols || y >= grid.rows) return null
  const index = y * grid.cols + x
  return grid.walkable[index] ? index : null
}

/** Клетки «кольца» радиуса r вокруг точки — обход по расширяющемуся квадрату. */
function ringCells(sx: number, sy: number, r: number): [number, number][] {
  const out: [number, number][] = []
  for (let dy = -r; dy <= r; dy++) {
    for (let dx = -r; dx <= r; dx++) {
      if (Math.abs(dx) === r || Math.abs(dy) === r) out.push([sx + dx, sy + dy])
    }
  }
  return out
}

/** Ближайшая проходимая ячейка к точке (вход мог оказаться в стене). */
function nearestWalkable(grid: Grid, p: MPoint): number | null {
  const [sx, sy] = cellOf(grid, p)
  const own = walkableAt(grid, sx, sy)
  if (own !== null) return own
  const limit = Math.max(grid.cols, grid.rows)
  for (let r = 1; r < limit; r++) {
    for (const [x, y] of ringCells(sx, sy, r)) {
      const found = walkableAt(grid, x, y)
      if (found !== null) return found
    }
  }
  return null
}

/** Смещения соседей: сначала прямые, потом диагонали. */
const STEPS: readonly [number, number][] = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
]

/** Соседи по 8 направлениям: по диагонали — только если оба «локтя» свободны. */
function neighbours(grid: Grid, index: number): [number, number][] {
  const cx = index % grid.cols
  const cy = Math.floor(index / grid.cols)
  const out: [number, number][] = []
  for (const [dx, dy] of STEPS) {
    const next = walkableAt(grid, cx + dx, cy + dy)
    if (next === null) continue
    const diagonal = dx !== 0 && dy !== 0
    // Наискось через угол между двумя стенами не проходим.
    if (
      diagonal &&
      (walkableAt(grid, cx + dx, cy) === null || walkableAt(grid, cx, cy + dy) === null)
    ) {
      continue
    }
    out.push([next, diagonal ? Math.SQRT2 : 1])
  }
  return out
}

/** Узел с наименьшей оценкой в открытом списке. */
function popBest(open: Map<number, number>): number {
  let node = -1
  let best = Number.POSITIVE_INFINITY
  for (const [candidate, score] of open) {
    if (score < best) {
      best = score
      node = candidate
    }
  }
  open.delete(node)
  return node
}

/** Развернуть путь из карты предков. */
function tracePath(from: Map<number, number>, goal: number): number[] {
  const path = [goal]
  let node = goal
  while (from.has(node)) {
    node = from.get(node) as number
    path.push(node)
  }
  return path.reverse()
}

/** A* по сетке; вернёт индексы ячеек или null, если пути нет. */
function astar(grid: Grid, start: number, goal: number): number[] | null {
  const gx = goal % grid.cols
  const gy = Math.floor(goal / grid.cols)
  const heuristic = (i: number): number =>
    Math.hypot((i % grid.cols) - gx, Math.floor(i / grid.cols) - gy)
  const open = new Map<number, number>([[start, heuristic(start)]])
  const gScore = new Map<number, number>([[start, 0]])
  const from = new Map<number, number>()

  while (open.size > 0) {
    const current = popBest(open)
    if (current === goal) return tracePath(from, goal)
    const g = gScore.get(current) ?? 0
    for (const [next, cost] of neighbours(grid, current)) {
      const tentative = g + cost
      if (tentative >= (gScore.get(next) ?? Number.POSITIVE_INFINITY)) continue
      from.set(next, current)
      gScore.set(next, tentative)
      open.set(next, tentative + heuristic(next))
    }
  }
  return null
}

/** Прямая между точками не пересекает препятствий (для сглаживания). */
function visible(grid: Grid, a: MPoint, b: MPoint): boolean {
  const steps = Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) / (grid.step * 0.5))
  for (let i = 1; i < steps; i++) {
    const t = i / steps
    const p: MPoint = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]
    const [cx, cy] = cellOf(grid, p)
    if (!grid.walkable[cy * grid.cols + cx]) return false
  }
  return true
}

/** Убрать лишние точки: пока видно следующую — промежуточные не нужны. */
function smooth(grid: Grid, points: readonly MPoint[]): MPoint[] {
  if (points.length <= 2) return [...points]
  const out: MPoint[] = [points[0] as MPoint]
  let i = 0
  while (i < points.length - 1) {
    let j = points.length - 1
    while (j > i + 1 && !visible(grid, points[i] as MPoint, points[j] as MPoint)) j--
    out.push(points[j] as MPoint)
    i = j
  }
  return out
}

/**
 * Путь по этажу от точки входа к арендатору. Возвращает точки в метрах
 * (система этажа) или null, если пройти нельзя.
 */
export function indoorPath(
  outline: readonly MPoint[],
  zones: readonly TenantZone[],
  from: MPoint,
  targetId: string,
): MPoint[] | null {
  const target = zones.find((z) => z.tenantId === targetId)
  if (!target) return null
  const grid = walkGrid(outline, zones, targetId)
  if (!grid) return null
  const start = nearestWalkable(grid, from)
  const goal = nearestWalkable(grid, target.center)
  if (start === null || goal === null) return null
  const cells = astar(grid, start, goal)
  if (!cells) return null
  const points = cells.map((i) => centerOf(grid, i % grid.cols, Math.floor(i / grid.cols)))
  const path = smooth(grid, points)
  // Начало и конец — настоящие точки, а не центры ячеек.
  path[0] = from
  path[path.length - 1] = target.center
  return path
}

/** Длина пути, м — показываем в подсказке «идти N м». */
export function pathLengthM(path: readonly MPoint[]): number {
  let sum = 0
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1] as MPoint
    const b = path[i] as MPoint
    sum += Math.hypot(b[0] - a[0], b[1] - a[1])
  }
  return sum
}
